import { Alert, Box, IconButton, Tooltip, Typography } from '@mui/material';
import { Edit as EditIcon } from '@mui/icons-material';
import { useCallback, useMemo } from 'react';
import { GangParticipant } from '../../types/gangCreationTypes';
import { PermissionGate } from '@billda/permissions-ui';

interface GangFinancialSummaryProps {
  leaders: GangParticipant[];
  members: GangParticipant[];
  isEqualSplit?: boolean;
  title?: string;
  testIdPrefix?: string;
  testIdSuffix?: string;
  showEditButton?: boolean;
  onEditClick?: () => void;
  editButtonTooltip?: string;
}

const formatCurrency = (amount: number) =>
  new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency: 'GBP',
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(amount);

export const GangFinancialSummary = ({
  leaders,
  members,
  isEqualSplit = false,
  title = 'Financial Summary',
  testIdPrefix,
  testIdSuffix,
  showEditButton = false,
  onEditClick,
  editButtonTooltip = 'Edit member configuration',
}: GangFinancialSummaryProps) => {
  const allMembers = useMemo(
    () => [...leaders, ...members],
    [leaders, members]
  );

  // When equal split is enabled, count by paymentType instead of stored percentage
  const membersWithPercentageSplit = useMemo(
    () =>
      allMembers.filter(member =>
        isEqualSplit
          ? member.paymentType === 'percentage'
          : member.defaultSplitPercentage !== null &&
            member.defaultSplitPercentage !== undefined
      ),
    [allMembers, isEqualSplit]
  );

  const membersWithFixedRate = useMemo(
    () =>
      allMembers.filter(
        member =>
          member.defaultRate !== null && member.defaultRate !== undefined
      ),
    [allMembers]
  );

  // When equal split is enabled, total is always 100%
  const totalPercentageSplit = useMemo(
    () =>
      isEqualSplit && membersWithPercentageSplit.length > 0
        ? 100
        : membersWithPercentageSplit.reduce(
            (sum, member) => sum + (member.defaultSplitPercentage || 0),
            0
          ),
    [membersWithPercentageSplit, isEqualSplit]
  );

  const totalFixedRate = useMemo(
    () =>
      membersWithFixedRate.reduce((sum, member) => {
        const rate = member.defaultRate || 0;
        const units = member.defaultUnitsValue || 0;
        return sum + rate * units;
      }, 0),
    [membersWithFixedRate]
  );

  const hasFinancialData = useMemo(
    () =>
      membersWithPercentageSplit.length > 0 || membersWithFixedRate.length > 0,
    [membersWithPercentageSplit.length, membersWithFixedRate.length]
  );

  const buildTestId = useCallback(
    (base: string) => `${testIdPrefix ?? ''}${base}${testIdSuffix ?? ''}`,
    [testIdPrefix, testIdSuffix]
  );

  return (
    <PermissionGate permission="gangs.manage">
      <Box
        sx={{ mt: 2 }}
        data-testid={buildTestId('financial-summary-container')}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 1,
            mb: 2,
          }}
        >
          <Typography
            variant="h6"
            color="primary"
            data-testid={buildTestId('financial-summary-title')}
            sx={{ flex: 1 }}
          >
            {title}
          </Typography>
          {showEditButton ? (
            <Tooltip title={editButtonTooltip}>
              <IconButton
                size="small"
                onClick={onEditClick}
                data-testid={buildTestId('financial-summary-edit-button')}
              >
                <EditIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          ) : null}
        </Box>

        {hasFinancialData ? (
          <>
            <Box
              sx={{
                display: 'flex',
                flexDirection: { xs: 'column', sm: 'row' },
                gap: 4,
                mt: 2,
              }}
            >
              {membersWithPercentageSplit.length > 0 && (
                <Box
                  data-testid={buildTestId(
                    'financial-summary-split-allocation-container'
                  )}
                >
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    data-testid={buildTestId(
                      'financial-summary-split-allocation-label'
                    )}
                  >
                    Total Split Allocated
                  </Typography>
                  <Typography
                    variant="h4"
                    color={
                      totalPercentageSplit === 100
                        ? 'success.main'
                        : 'warning.main'
                    }
                    data-testid={buildTestId(
                      'financial-summary-split-allocation-value'
                    )}
                    data-percentage={Number(totalPercentageSplit.toFixed(2))}
                  >
                    {totalPercentageSplit.toFixed(2)}%
                  </Typography>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    data-testid={buildTestId(
                      'financial-summary-split-allocation-count'
                    )}
                    data-count={membersWithPercentageSplit.length}
                  >
                    {membersWithPercentageSplit.length} member
                    {membersWithPercentageSplit.length === 1 ? '' : 's'} with
                    splits
                  </Typography>
                </Box>
              )}

              {membersWithFixedRate.length > 0 && (
                <Box
                  data-testid={buildTestId(
                    'financial-summary-fixed-rates-container'
                  )}
                >
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    data-testid={buildTestId(
                      'financial-summary-fixed-rates-label'
                    )}
                  >
                    Total Fixed Rates
                  </Typography>
                  <Typography
                    variant="h4"
                    color="secondary.main"
                    data-testid={buildTestId(
                      'financial-summary-fixed-rates-value'
                    )}
                    data-total-fixed-rate={totalFixedRate}
                  >
                    {formatCurrency(totalFixedRate)}
                  </Typography>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    data-testid={buildTestId(
                      'financial-summary-fixed-rates-count'
                    )}
                    data-count={membersWithFixedRate.length}
                  >
                    {membersWithFixedRate.length} member
                    {membersWithFixedRate.length === 1 ? '' : 's'} with fixed
                    rate{membersWithFixedRate.length === 1 ? '' : 's'}
                  </Typography>
                </Box>
              )}
            </Box>

            {totalPercentageSplit > 0 && totalPercentageSplit !== 100 && (
              <Alert
                severity="warning"
                sx={{ mt: 2 }}
                data-testid={buildTestId(
                  'financial-summary-split-allocation-warning'
                )}
              >
                Default percentage splits total{' '}
                {totalPercentageSplit.toFixed(2)}
                %. Configuring the gang so that the full 100% is allocated will
                help speed up the pot splitting process.
              </Alert>
            )}
          </>
        ) : (
          <Alert
            severity="info"
            sx={{ mt: 2 }}
            data-testid={buildTestId('financial-summary-empty-message')}
          >
            No financial information has been configured for this gang yet.
          </Alert>
        )}
      </Box>
    </PermissionGate>
  );
};
